import { NOT_SET } from '@academysports/utils-lib/src/tealium/constants';
import { isLoggedIn, getRegistrationStatus } from './UserSession';
import StorageManager from './StorageManager';
import { getSelectedStoreFromCookies } from './cookies/SelectedStore';
import { enableMetaTracking } from './profileDataCapture';
import { USER_PROFILE_DATA, phoneNumber, zipCode } from './tealiumdatahelpers/tealiumConstants';

/*
Some general technical notes and diagrams are in confluence at:
https://academysports.atlassian.net/wiki/spaces/DD/pages/2814935063/Tealium+Tag+Manager
*/

/**
 * Gets a simple object with analytics fields about the current user on the page. IMPORTANT: these fields cannot
 * be determined server side due to page response caching effects. On the initial server render, the values
 * of any such fields are probably set to the 'not set' placeholder, and should be updated before the "view"
 * event for the page is fired.
 *
 * @returns object with fields about the browsing user
 */
export const getUserMetadata = () => {
  const visitorStatus = isLoggedIn() ? 'authenticated' : 'not authenticated';
  const userType = StorageManager.getCookie('USERTYPE');
  let visitorId = StorageManager.getCookie('USERACTIVITY') || 'guest';
  if (userType === 'G' || visitorId === '-1002') {
    visitorId = 'guest';
  }
  const registrationStatus = getRegistrationStatus() ? 'registered' : 'not registered';
  const authenticationMethod = isLoggedIn() ? 'email' : 'guest';
  const storeObj = getSelectedStoreFromCookies();
  const storeSelected = storeObj?.neighborhood || NOT_SET;

  const filteredUserProfileData = {};
  let localStorageUserProfileData;

  try {
    localStorageUserProfileData = enableMetaTracking() ? StorageManager.getLocalStorage(USER_PROFILE_DATA) : null;

    if (localStorageUserProfileData) {
      const userProfileData = localStorageUserProfileData;

      Object.keys(userProfileData).forEach(key => {
        let filteredKey = key;
        if (key === zipCode) {
          filteredKey = 'zip';
        } else if (key === phoneNumber) {
          filteredKey = 'phone';
        }
        // Convert value to lowercase before assigning to filteredUserProfileData
        filteredUserProfileData[filteredKey] = userProfileData[key]?.toLowerCase();
      });
    }
  } catch (error) {
    console.error('Error occurred while accessing local storage:', error);
  }

  return {
    visitorStatus,
    visitorId,
    registrationStatus,
    authenticationMethod,
    storeSelected,
    ...filteredUserProfileData,
  };
};

// Returns userProfileData from local storage when ENABLE_META_TRACKING is true.
export const getUserProfileData = () => {
  const userProfileData = {};
  try {
    const storedUserProfileData = StorageManager.getLocalStorage(USER_PROFILE_DATA);
    if (enableMetaTracking() && storedUserProfileData) {
      // Include all keys and value.
      Object.keys(storedUserProfileData).forEach(key => {
        const value = storedUserProfileData[key];
        // Convert value to lowercase before adding to userProfileData.
        userProfileData[key] = value?.toLowerCase();
      });
    }
  } catch (error) {
    console.error('Error occurred while accessing local storage:', error);
  }
  return userProfileData;
};

/**
 * For use when page view is deferred. This will get the original values to help you get started with your custom
 * page load event.
 * @returns
 */
export const getPageLoadData = (isPageCategory = false) => {
  const { utag_data = {} } = window;
  const pageCatParameter = {};

  if (isPageCategory) {
    pageCatParameter.pageCategory = utag_data.pageCategory;
  }
  const menuNavigationHierarchyFromNode = utag_data.menuNavigationHierarchy;
  if (menuNavigationHierarchyFromNode) {
    pageCatParameter.menuNavigationHierarchy = menuNavigationHierarchyFromNode;
  }
  const globalAnalyticsData = {
    pageName: utag_data.pageName,
    pageType: utag_data.pageType,
    pageTitle: utag_data.pageTitle,
    pageHierarchy: utag_data.pageHierarchy,
    pageEnv: utag_data.pageEnv,
    visitorStatus: utag_data.visitorStatus,
    visitorId: utag_data.visitorId,
    registrationStatus: utag_data.registrationStatus,
    authenticationMethod: utag_data.authenticationMethod,
    storeSelected: utag_data.storeSelected,
    componentsViewed: utag_data.componentsViewed,
    ...pageCatParameter,
  };

  // Combine global analytics data with userProfileData.
  const dataWithGlobalAndUserProfileData = { ...globalAnalyticsData, ...getUserProfileData() };
  return dataWithGlobalAndUserProfileData;
};
